import { Autocomplete } from 'stimulus-autocomplete'
import { withBreadcrumb } from '../../../javascript/mixins/breadcrumb'
import { withErrorHandler } from '../../../javascript/mixins/error_handler'

const optionSelector = "[role='option']:not([aria-disabled])"

/**
 * Autocomplete Field Controller
 *
 * Extends stimulus-autocomplete with:
 * - Mouse hover support for selecting options (workaround for upstream issue #125)
 * - Committed-value tracking for hidden-value fields: the upstream controller
 *   wipes the hidden value on every keystroke, so submitting after typing
 *   without a fresh dropdown pick posts an empty id (#857). Instead, typing
 *   never changes the hidden value; fully clearing the text clears it
 *   intentionally, and uncommitted partial text marks the field invalid so
 *   constraint validation blocks any submit until the user picks a suggestion
 *   or empties the field.
 * - Breadcrumb tracking for debugging autocomplete interactions
 * - Error handling for fetch failures and DOM operations
 *
 * Uses mixin composition to add breadcrumb tracking and error handling
 * to the third-party Autocomplete controller.
 *
 * @see {@link https://github.com/afcapel/stimulus-autocomplete}
 * @extends {Autocomplete}
 */
export default class extends withErrorHandler(withBreadcrumb(Autocomplete)) {
  static values = { uncommittedMessage: { type: String, default: '' } }

  connect() {
    super.connect()

    // Add hover support for option selection
    // TODO: Remove this when https://github.com/afcapel/stimulus-autocomplete/issues/125 is resolved
    this.resultsTarget.addEventListener('mouseover', this.onResultsMouseOver)

    // Track autocomplete selections using the library's custom event
    // This event fires once per committed selection (click, Enter, Tab)
    this.element.addEventListener('autocomplete.change', this.onAutocompleteChange)

    if (this.hasHiddenTarget) {
      this.committed = { label: this.inputTarget.value, value: this.hiddenTarget.value }
      // Registered besides the (debounced) upstream input listener: an
      // intentional full clear must reach the hidden value immediately.
      this.inputTarget.addEventListener('input', this.onInputSync)
      this.formElement = this.inputTarget.form
      this.formElement?.addEventListener('reset', this.onFormReset)
      this.syncValidity()
    }
  }

  disconnect() {
    super.disconnect()

    if (this.hasResultsTarget) {
      this.resultsTarget.removeEventListener('mouseover', this.onResultsMouseOver)
    }

    if (this.element) {
      this.element.removeEventListener('autocomplete.change', this.onAutocompleteChange)
    }

    if (this.hasInputTarget) {
      this.inputTarget.removeEventListener('input', this.onInputSync)
    }

    this.formElement?.removeEventListener('reset', this.onFormReset)
  }

  /**
   * Replaces the upstream input handler — the upstream connect() wraps this
   * class field in its debounce. Identical to upstream minus the
   * unconditional wipe of the hidden value, which must survive mere typing.
   */
  onInputChange = () => {
    const query = this.inputTarget.value.trim()
    if (query && query.length >= this.minLengthValue) {
      this.fetchResults(query)
    } else {
      this.hideAndRemoveOptions()
    }
  }

  /**
   * Opens the full list from the chevron, with no typing required — the only
   * way to discover what a filter offers when its options live behind a
   * server-backed autocomplete rather than a <select>.
   *
   * Deliberately bypasses minLengthValue: that gate exists to avoid firing a
   * request per keystroke, which does not apply to an explicit click. Focus
   * must move to the input, because close-on-outside-click hangs off the
   * input's blur and Escape off its keydown — a list opened with focus left on
   * the button could never be dismissed.
   */
  /**
   * Keeps the press on the chevron from blurring the input. Upstream closes
   * the list on the input's blur, which fires on mousedown — before the click
   * handler runs — so without this the toggle below always reads a closed
   * list and reopens it, and the chevron can never close what it opened.
   * Upstream guards its own results list the same way, via `mouseDown`.
   */
  holdFocus = (event) => {
    event.preventDefault()
  }

  showAll = () => {
    if (this.resultsShown) {
      this.hideAndRemoveOptions()
      return
    }

    this.inputTarget.focus()
    this.fetchResults(this.inputTarget.value.trim())
  }

  /**
   * Fully emptied text is an intentional clear of the hidden value — applied
   * immediately (not debounced) so a fast follow-up submit posts an empty id.
   * Everything else re-evaluates the field's validity.
   */
  onInputSync = () => {
    if (this.inputTarget.value.trim() === '') {
      this.hiddenTarget.value = ''
      this.committed = { label: '', value: '' }
    }

    this.syncValidity()
  }

  /**
   * Clears the pending-edit validity before the upstream commit dispatches
   * autocomplete.change: an auto-submit bound to that event may run before
   * this controller's own listener, and must not be blocked by the validity
   * of the text the user just replaced by picking a suggestion.
   *
   * @param {Element} selected - The picked suggestion element
   */
  commit(selected) {
    if (this.hasHiddenTarget) this.inputTarget.setCustomValidity('')

    super.commit(selected)
  }

  /**
   * Marks the visible field invalid while its text is a non-empty edit that
   * was never committed via the suggestion list. Constraint validation then
   * blocks every submit path with a native message — the hidden value still
   * holds the committed id, so nothing can be submitted silently.
   */
  syncValidity() {
    const text = this.inputTarget.value.trim()
    const uncommitted = text !== '' && text !== this.committed.label.trim()

    this.inputTarget.setCustomValidity(uncommitted ? this.uncommittedMessageValue : '')
  }

  /**
   * A native form reset restores defaultValue on both fields; re-sync the
   * committed pair from those (defaultValue is unaffected by event order).
   * Text and label are back in sync afterwards, so the field is valid again.
   */
  onFormReset = () => {
    this.committed = { label: this.inputTarget.defaultValue, value: this.hiddenTarget.defaultValue }
    this.inputTarget.setCustomValidity('')
  }

  /**
   * Tracks autocomplete selection events for debugging and records the
   * committed selection that blur re-syncs to.
   * Fires when user commits a selection via click, keyboard, or hover.
   *
   * @param {CustomEvent} event - The autocomplete.change event
   * @param {Object} event.detail - Selection details
   * @param {string} event.detail.value - The selected value
   * @param {string} event.detail.textValue - The selected text label
   * @param {HTMLElement} event.detail.selected - The selected option element
   */
  onAutocompleteChange = (event) => {
    const { value, textValue } = event.detail

    if (this.hasHiddenTarget) {
      this.committed = { label: textValue, value }
      this.syncValidity()
    }

    this.addBreadcrumb('Form', 'autocomplete_selection', {
      value,
      textValue,
      fieldName: this.inputTarget.name,
    })
  }

  /**
   * Handles mouseover events on results list to select options on hover.
   * Temporary workaround for upstream issue.
   *
   * @param {MouseEvent} event - The mouseover event
   * @see {@link https://github.com/afcapel/stimulus-autocomplete/issues/125}
   */
  onResultsMouseOver = (event) => {
    try {
      if (!(event.target instanceof Element)) return

      const selected = event.target.closest(optionSelector)
      if (selected) this.select(selected)
    } catch (error) {
      this.reportError(error, 'Error selecting autocomplete option on hover', {
        targetElement: event.target?.tagName,
      })
    }
  }
}
